import { inject, Injectable, DOCUMENT } from '@angular/core';

import { ConfigurableFocusTrapFactory } from '@angular/cdk/a11y';
import { ModalService } from '../modal/modal.service';

import type { ConfigurableFocusTrap } from '@angular/cdk/a11y';

export interface TrapOptions {
  autoFocus: boolean;
  hideModalFromScreenReader?: boolean;
  defer?: boolean;
}

@Injectable({ providedIn: 'root' })
export class FocusTrapperFactory {
  private readonly document: Document = inject(DOCUMENT, { optional: true });
  private readonly focusTrapFactory = inject(ConfigurableFocusTrapFactory);
  private readonly modalService = inject(ModalService);

  create(element: HTMLElement, trapOptions?: TrapOptions): FocusTrapper {
    const focusTrap = this.focusTrapFactory.create(element, { defer: trapOptions?.defer || false } );
    return new FocusTrapper(element, this.document, focusTrap, this.modalService, trapOptions);
  }
}

export class FocusTrapper {
  private elementFocusedBefore: HTMLElement | null = null;

  constructor(private element: HTMLElement,
              private document: Document,
              private focusTrap: ConfigurableFocusTrap,
              private modalService: ModalService,
              private trapOptions?: TrapOptions) {
  }

  destroy(): void {
    this.focusTrap.destroy();
  }

  /**
   * Autofocus the first tabbable element inside of the modal, if there is not a tabbable element,
   * focus the modal instead.
   */
  autoFocusFirstTabbableElement(): void {
    // If were to attempt to focus immediately, then the content of the modal would not yet be
    // ready in instances where change detection has to run first. To deal with this, we simply
    // wait for the microtask queue to be empty.

    if (!this.focusTrap.hasAttached()) {
      this.focusTrap.attachAnchors();
    }

    if ((this.trapOptions?.autoFocus) ?? false) {
      this.focusTrap.focusInitialElementWhenReady().then((hasMovedFocus: boolean): void => {
        // If we didn't find any focusable elements inside the modal, focus the
        // container so the user can't tab into other elements behind it.
        if (!hasMovedFocus) {
          this.element.focus();
        }
      });
    }
  }

  /** Returns the focus to the element focused before the modal was open. */
  returnFocusAfterClose(): void {
    const toFocus = this.elementFocusedBefore;
    // We need the extra check, because IE can set the `activeElement` to null in some cases.
    if (((this.trapOptions?.hideModalFromScreenReader) ?? false) && this.modalService.openModals.length > 0) {
      this.modalService.toggleScreenReaderVisibility(true);
    }
    if (toFocus !== undefined && typeof toFocus?.focus === 'function') {
      toFocus.focus();
    }
  }

  /** Saves a reference to the element that was focused before the modal was opened. */
  savePreviouslyFocusedElement(): void {
    if (this.document !== undefined) {
      if (((this.trapOptions?.hideModalFromScreenReader) ?? false) && this.modalService.openModals.length > 0) {
        this.modalService.toggleScreenReaderVisibility(false);
      }
      this.elementFocusedBefore = this.document.activeElement as HTMLElement;

      // Move focus onto the modal immediately in order to prevent the user from accidentally
      // opening multiple modal at the same time. Needs to be async, because the element
      // may not be focusable immediately.
      Promise.resolve().then((): void => this.element.focus());
    }
  }

}
